'use client';

/**
 * CampSwapSheet: "pick another camp for tonight".
 *
 * Desktop: a side sheet laid over the itinerary column, so the live map
 * beside it stays in view. The rows are numbered and the map plots the same
 * numbers (onPins); hovering a row or a pin lights both.
 * Phones: a bottom sheet over the list.
 *
 * Every row leads with a 56px photo (or a tinted tile for its kind of
 * place) carrying the pin number the map shows. Hover or focus a row and its
 * pin lights; click a pin and the list scrolls to its row and holds it.
 *
 * Sort (recommended / nearest / price) and filter (powered, book online) sit
 * in one row that scrolls sideways. Every row says its price and how far it
 * is from tonight's stop ("2.1 km from Lakes Entrance").
 * Escape / backdrop closes; focus starts on the dialog (nothing looks
 * pre-picked) and returns to the Swap button on close.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { filterCamps, type CampOption, type LatLng } from '../../utils/campsAlong';
import { CampFacts, CampThumb, campHref } from './CampBits';
import { IconCheck, IconX } from './icons';
import { useSheetOpen } from './useSheetOpen';

type Sort = 'best' | 'nearest' | 'price';

/** A night's nearby camps: still loading, in, or the search failed. */
export type NightCampStatus = 'loading' | 'ready' | 'error';

interface Props {
  kicker: string;
  /** Tonight's stop, by name ("Lakes Entrance"). */
  placeName?: string;
  /** Tonight's stop, on the map strip. */
  anchor?: LatLng | null;
  current: CampOption | null;
  /** The current camp is on the trip (else it's our suggestion). */
  currentChosen?: boolean;
  options: CampOption[];
  distanceFor: (c: CampOption) => string | null;
  kmFor?: (c: CampOption) => number | null;
  guest?: boolean;
  /** More camps still on their way: loading rows under the current one. */
  loading?: boolean;
  /** The camp search failed: say so, with a retry. */
  error?: boolean;
  onRetry?: () => void;
  onChoose: (c: CampOption) => void;
  onClose: () => void;
  /** The rows, numbered: the live map plots exactly these while open. */
  onPins?: (rows: { camp: CampOption; n: number }[]) => void;
  /** Lit from the map. */
  hoveredId?: number | null;
  onHover?: (id: number | null) => void;
  /** Picked on the map (sticky until another pick). */
  selectedId?: number | null;
  onSelect?: (id: number | null) => void;
}

export function Chip({
  active,
  onClick,
  children,
  pressed,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  /** Toggle (aria-pressed) rather than one-of-many (aria-checked). */
  pressed?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      {...(pressed ? { 'aria-pressed': active } : { role: 'radio', 'aria-checked': active })}
      className={`inline-flex h-8 min-h-0 flex-none items-center gap-1 whitespace-nowrap rounded-full border border-solid px-3 text-tp-xs font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-tp-brand ${
        active ? 'border-tp-ink bg-tp-ink text-white' : 'border-tp-line-strong bg-white text-tp-muted hover:border-tp-line-strong hover:bg-tp-surface'
      }`}
    >
      {pressed && active && <IconCheck className="h-3 w-3" />}
      {children}
    </button>
  );
}

export function sortCampOptions(options: CampOption[], sort: Sort, kmFor?: (c: CampOption) => number | null): CampOption[] {
  if (sort === 'best') return options;
  const out = [...options];
  if (sort === 'nearest' && kmFor) {
    out.sort((a, b) => (kmFor(a) ?? Infinity) - (kmFor(b) ?? Infinity));
  } else if (sort === 'price') {
    out.sort((a, b) => (a.minPrice ?? Infinity) - (b.minPrice ?? Infinity));
  }
  return out;
}

/**
 * "2.1 km from Lakes Entrance" / "In Lakes Entrance". `short` drops the
 * place ("2.1 km away") where the heading already names it (the Swap sheet).
 */
export function fromStopLabel(km: number | null | undefined, placeName?: string, short = false): string | null {
  if (typeof km !== 'number' || !Number.isFinite(km)) return null;
  const where = placeName || 'tonight’s stop';
  if (km < 0.5) return `In ${where}`;
  const d = `${km < 10 ? km.toFixed(1) : Math.round(km)} km`;
  return short ? `${d} away` : `${d} from ${where}`;
}

const SORT_HINT: Record<Sort, string> = {
  best: 'Close to tonight’s stop, with photos first.',
  nearest: 'Nearest to tonight’s stop first.',
  price: 'Cheapest listed price first. Camps without a price come last.',
};

/** Where the sheet goes: over the itinerary column on desktop, a bottom sheet on phones. */
export type SheetFrame =
  | { mode: 'side'; left: number; top: number; width: number; height: number }
  | { mode: 'center' }
  | { mode: 'bottom' };

export function sheetFrame(
  column: { left: number; top: number; width: number; height: number } | null,
  viewport: { width: number; height: number }
): SheetFrame {
  if (viewport.width < 768) return { mode: 'bottom' };
  // A column wider than a sheet (list view) or off screen: a centred dialog.
  if (!column || column.width > 600 || column.width < 320) return { mode: 'center' };
  const top = Math.max(0, column.top);
  const bottom = Math.min(viewport.height, column.top + column.height);
  if (bottom - top < 360) return { mode: 'center' };
  return { mode: 'side', left: column.left, top, width: column.width, height: bottom - top };
}

/** The itinerary column, by marker; null when it isn't on screen. */
export function columnRect() {
  const el = typeof document !== 'undefined' ? document.querySelector<HTMLElement>('[data-planner-column]') : null;
  if (!el || !el.offsetParent) return null;
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

export default function CampSwapSheet({
  kicker,
  placeName,
  current,
  currentChosen = false,
  options,
  distanceFor,
  kmFor,
  guest,
  loading = false,
  error = false,
  onRetry,
  onChoose,
  onClose,
  onPins,
  hoveredId,
  onHover,
  selectedId,
  onSelect,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const headingId = useRef(`swap-${Math.random().toString(36).slice(2, 8)}`).current;
  const [sort, setSort] = useState<Sort>('best');
  const [powered, setPowered] = useState(false);
  const [bookable, setBookable] = useState(false);
  const [localLit, setLocalLit] = useState<number | null>(null);
  const litId = hoveredId ?? localLit ?? selectedId ?? null;
  const [frame, setFrame] = useState<SheetFrame>(() =>
    typeof window === 'undefined' ? { mode: 'bottom' } : sheetFrame(columnRect(), { width: window.innerWidth, height: window.innerHeight })
  );
  useSheetOpen();

  const hasPrices = options.filter((o) => typeof o.minPrice === 'number').length >= 2;
  const anyPowered = options.some((o) => o.powered === true);
  const anyBookable = options.some((o) => !!o.bookingUrl);
  const rows = useMemo(
    () => sortCampOptions(filterCamps(options, { powered, bookable }), sort, kmFor),
    [options, powered, bookable, sort, kmFor]
  );
  const firstChoiceId = rows.find((r) => r.id !== current?.id)?.id ?? null;
  const side = frame.mode === 'side';
  // Numbers only where the map beside the sheet shows the same numbers.
  const numbered = side && !!onPins;

  // Follow the column as the page scrolls or resizes.
  useEffect(() => {
    const update = () => setFrame(sheetFrame(columnRect(), { width: window.innerWidth, height: window.innerHeight }));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, []);

  // The live map plots the rows on screen, numbered as here.
  const rowsKey = rows.map((r) => r.id).join(',');
  useEffect(() => {
    if (!numbered) return;
    onPins?.(rows.map((camp, i) => ({ camp, n: i + 1 })));
    // rowsKey stands in for `rows`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsKey, numbered, onPins]);
  useEffect(() => () => onPins?.([]), [onPins]);

  // A pin picked on the map brings its row into view (hover alone doesn't
  // scroll: the list would chase the pointer across the map).
  useEffect(() => {
    if (selectedId == null) return;
    listRef.current?.querySelector<HTMLElement>(`[data-camp-id="${selectedId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId]);
  useEffect(() => () => onSelect?.(null), [onSelect]);

  const light = (id: number | null) => {
    setLocalLit(id);
    onHover?.(id);
  };

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    // Focus the dialog itself, not the first "Choose" (a ring on a row read
    // as a camp already picked) nor Close (a ring there looked like an error).
    panelRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const f = Array.from(panelRef.current.querySelectorAll<HTMLElement>('button, a[href]')).filter((el) => !el.hasAttribute('disabled'));
      if (!f.length) return;
      const a = f[0];
      const z = f[f.length - 1];
      if (e.shiftKey && document.activeElement === a) {
        e.preventDefault();
        z.focus();
      } else if (!e.shiftKey && document.activeElement === z) {
        e.preventDefault();
        a.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      onHover?.(null);
      if (prev && document.contains(prev)) prev.focus?.();
    };
    // onHover is a setter; only onClose changing should re-bind.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  const panelClass =
    frame.mode === 'side'
      ? 'fixed flex flex-col overflow-hidden bg-white shadow-[-12px_0_32px_-16px_rgba(16,24,40,0.35)] outline-none'
      : frame.mode === 'center'
        ? 'relative flex max-h-[86vh] w-full max-w-[480px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl outline-none'
        : 'relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl outline-none';
  const panelStyle: React.CSSProperties =
    frame.mode === 'side'
      ? { left: frame.left, top: frame.top, width: frame.width, height: frame.height, animation: 'tpSlideIn 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both' }
      : { animation: 'tpFadeUp 200ms cubic-bezier(0.2, 0.8, 0.2, 1) both' };

  const sheet = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal={side ? undefined : true}
      aria-labelledby={headingId}
      tabIndex={-1}
      data-testid="camp-swap-sheet"
      data-frame={frame.mode}
      className={panelClass}
      style={{ ...panelStyle, outline: 'none' }}
    >
      {frame.mode === 'bottom' && <div className="mx-auto mt-2 h-1 w-10 flex-none rounded-full bg-tp-line" aria-hidden="true" />}
      <header className="flex-none border-b border-tp-line px-4 pb-3 pt-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-tp-sm font-medium text-tp-muted">{kicker}</p>
            <h2 id={headingId} className="mt-0.5 text-[17px] font-semibold leading-tight tracking-tight text-tp-ink">
              Where to stay tonight
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1.5 inline-flex h-9 w-9 min-h-0 flex-none items-center justify-center rounded-tp text-tp-muted hover:bg-tp-surface hover:text-tp-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-tp-brand"
          >
            <IconX className="h-4 w-4" />
          </button>
        </div>
        {/* One row that scrolls sideways: never a chip orphaned on a second line. */}
        <div className="tp-chip-row -mx-4 mt-3 flex gap-1.5 overflow-x-auto pl-4" role="toolbar" aria-label="Sort and filter">
          <span role="radiogroup" aria-label="Sort" className="flex flex-none gap-1.5">
            <Chip active={sort === 'best'} onClick={() => setSort('best')} title={SORT_HINT.best}>Recommended</Chip>
            {kmFor && <Chip active={sort === 'nearest'} onClick={() => setSort('nearest')} title={SORT_HINT.nearest}>Nearest</Chip>}
            {hasPrices && <Chip active={sort === 'price'} onClick={() => setSort('price')} title={SORT_HINT.price}>Price</Chip>}
          </span>
          {(anyPowered || anyBookable) && <span className="mx-0.5 w-px flex-none self-stretch bg-tp-line" aria-hidden="true" />}
          {anyPowered && (
            <Chip pressed active={powered} onClick={() => setPowered((v) => !v)}>Powered</Chip>
          )}
          {anyBookable && (
            <Chip pressed active={bookable} onClick={() => setBookable((v) => !v)}>Book online</Chip>
          )}
          <span className="w-6 flex-none" aria-hidden="true" />
        </div>
      </header>
      <ul ref={listRef} className="tp-touch-scroll flex-1 divide-y divide-tp-line overflow-y-auto overscroll-contain" aria-live="polite">
        {loading &&
          [0, 1, 2].map((k) => (
            <li key={`l${k}`} className="flex items-center gap-3 px-4 py-3" aria-hidden="true">
              <div className="h-14 w-14 flex-none animate-pulse rounded-tp bg-tp-surface" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-3/4 animate-pulse rounded bg-tp-surface" />
                <div className="h-2.5 w-1/2 animate-pulse rounded bg-tp-surface" />
              </div>
              <div className="h-9 w-[84px] animate-pulse rounded-tp bg-tp-surface" />
            </li>
          ))}
        {error && !loading && rows.length === 0 && (
          <li className="px-4 py-6 text-center text-tp-sm text-tp-muted">
            Couldn’t reach the camp search.{' '}
            {onRetry && (
              <button type="button" className="border-0 bg-transparent p-0 font-semibold text-tp-brand hover:underline" onClick={onRetry}>
                Try again
              </button>
            )}
          </li>
        )}
        {!error && !loading && options.length === 0 && (
          <li className="px-4 py-6 text-center text-tp-sm text-tp-muted">
            No camps listed within 25 km of {placeName || 'this stop'} yet.
          </li>
        )}
        {rows.length === 0 && !loading && !error && options.length > 0 && (
          <li className="px-4 py-6 text-center text-tp-sm text-tp-muted">
            No camps here match.{' '}
            <button
              type="button"
              className="font-semibold text-tp-brand hover:underline"
              onClick={() => {
                setPowered(false);
                setBookable(false);
              }}
            >
              Clear filters
            </button>
          </li>
        )}
        {rows.map((c, i) => {
          const isCurrent = current?.id === c.id;
          const lit = litId === c.id;
          // One filled button at a time: the lit row's, else the top choice.
          const filled = litId != null ? lit : c.id === firstChoiceId;
          const away = fromStopLabel(kmFor?.(c), placeName, true) ?? distanceFor(c);
          return (
            <li
              key={c.id}
              className={`flex items-center gap-3 px-4 py-3 transition-colors duration-150 ${
                isCurrent ? 'bg-tp-ok-soft' : lit ? 'bg-tp-brand-soft shadow-[inset_3px_0_0_#1558b0]' : ''
              }`}
              style={{ scrollMarginTop: 8 }}
              data-testid="camp-swap-option"
              data-camp-id={c.id}
              // A real mouse only: a tap fires mouseenter too and left the
              // row lit (and underlined) after the finger lifted.
              onPointerEnter={(e) => e.pointerType === 'mouse' && light(c.id)}
              onPointerLeave={(e) => e.pointerType === 'mouse' && light(null)}
              onFocus={(e) => e.target.matches(':focus-visible') && light(c.id)}
              onBlur={() => light(null)}
            >
              <CampThumb camp={c} size={56} n={numbered ? i + 1 : undefined} lit={lit} tone={isCurrent ? 'green' : 'blue'} />
              <div className="min-w-0 flex-1">
                <a
                  href={campHref(c)}
                  target="_blank"
                  rel="noopener"
                  className="line-clamp-2 text-tp-base font-semibold leading-snug text-tp-ink no-underline visited:text-tp-ink hover:underline hover:decoration-tp-faint hover:underline-offset-2"
                >
                  {c.name}
                </a>
                <CampFacts camp={c} distance={away} />
              </div>
              {isCurrent ? (
                <span className="inline-flex h-9 flex-none items-center gap-1 rounded-tp px-2 text-tp-sm font-semibold text-tp-ok">
                  <IconCheck className="h-3.5 w-3.5" /> {currentChosen ? 'Staying' : 'Our pick'}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onChoose(c)}
                  aria-label={`Stay at ${c.name} tonight${guest ? ' (kept on this device)' : ''}`}
                  className={`inline-flex h-9 min-h-0 flex-none items-center rounded-tp border border-solid px-3 text-tp-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-tp-brand ${
                    filled
                      ? 'border-tp-brand bg-tp-brand text-white hover:bg-tp-brand-ink'
                      : 'border-tp-line-strong bg-white text-tp-ink hover:border-tp-brand hover:text-tp-brand'
                  }`}
                  data-testid="camp-swap-choose"
                >
                  Stay here
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <footer className="flex-none border-t border-tp-line px-4 py-2.5 text-tp-xs leading-snug text-tp-muted">
        Camps within 25 km of {placeName || 'tonight’s stop'}. Rates come from each listing; check before you go.
      </footer>
    </div>
  );

  return createPortal(
    side ? (
      // Over the itinerary column only: the map beside it stays live.
      <div className="pointer-events-none fixed inset-0 z-[1200]" role="presentation">
        <button
          type="button"
          aria-label="Close"
          tabIndex={-1}
          onClick={onClose}
          className="pointer-events-auto fixed cursor-default bg-tp-ink/25"
          style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height, animation: 'tpFadeIn 160ms ease-out both' }}
        />
        <div className="pointer-events-auto">{sheet}</div>
      </div>
    ) : (
      <div className={`fixed inset-0 z-[1200] flex justify-center ${frame.mode === 'center' ? 'items-center' : 'items-end'}`} role="presentation">
        <button
          type="button"
          aria-label="Close"
          tabIndex={-1}
          onClick={onClose}
          className="absolute inset-0 h-full w-full cursor-default bg-tp-ink/40"
          style={{ animation: 'tpFadeIn 160ms ease-out both' }}
        />
        {sheet}
      </div>
    ),
    document.body
  );
}
